import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { Prices } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'
import { queryKeys } from '@/react-query/queries/prices/keys'

/**
 * Invalidate price validation data and impact data for a given fund and dates.
 */
const useInvalidatePriceValidationData = (params?: Prices.GetPriceValidationParams) => {
  const queryClient = useQueryClient()

  return () => {
    if (params) {
      queryClient.invalidateQueries({ queryKey: queryKeys.eod.validation(params) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eod.validationImpact(params) })
    }
  }
}

export const useUpdatePriceValidationEntryMutation = (params?: Prices.GetPriceValidationParams) => {
  const api = useApi()

  const invalidate = useInvalidatePriceValidationData(params)

  return useMutation({
    mutationFn: (params: Prices.UpdateValidationEntryBody) => {
      return api.prices.eod.updateValidationEntry(params)
    },
    onSuccess: (_data, params, _context) => {
      toast.success(`Successfully updated ${params.oldValue} to ${params.newValue}`)
    },
    onError: (error, params, _context) => {
      toastApiError(error, `Error updating ${params.oldValue} to ${params.newValue}`)
    },
    onSettled: (_data, _error, _params) => {
      invalidate()
    },
  })
}

export const useResetPriceValidationEntryMutation = (params?: Prices.GetPriceValidationParams) => {
  const api = useApi()

  const invalidate = useInvalidatePriceValidationData(params)

  return useMutation({
    mutationFn: (params: Prices.ResetValidationEntryParams) => {
      return api.prices.eod.resetValidationEntry(params)
    },
    onSuccess: (_data, params, _context) => {
      toast.success(`Successfully reset prices for ${params.entry_id}`)
    },
    onError: (error, params, _context) => {
      toastApiError(error, `Error resetting prices for ${params.entry_id}`)
    },
    onSettled: (_data, _error, _params) => {
      invalidate()
    },
  })
}

export const useValidatePriceValidationEntryMutation = (
  params?: Prices.GetPriceValidationParams,
) => {
  const api = useApi()

  const invalidate = useInvalidatePriceValidationData(params)

  return useMutation({
    mutationFn: (params: Prices.ValidateEntryParams) => {
      return api.prices.eod.validateValidationEntry(params)
    },
    onSuccess: (_data, params, _context) => {
      toast.success(`Successfully validated ${params.entry_id}`)
    },
    onError: (error, params, _context) => {
      toastApiError(error, `Error validating entry ${params.entry_id}`)
    },
    onSettled: (_data, _error, _params) => {
      invalidate()
    },
  })
}

export const useValidateAllPricesMutation = (params?: Prices.GetPriceValidationParams) => {
  const api = useApi()

  const invalidate = useInvalidatePriceValidationData(params)

  return useMutation({
    mutationFn: (params: Prices.ValidateAllPricesBody) => {
      return api.prices.eod.validateAllPrices(params)
    },
    onSuccess: (_data, params, _context) => {
      toast.success(
        `Successfully validated all prices for ${params.fund_name} on ${params.value_date}`,
      )
    },
    onError: (error, params, _context) => {
      toastApiError(
        error,
        `Error validating prices for ${params.fund_name} on ${params.value_date}`,
      )
    },
    onSettled: (_data, _error, _params) => {
      invalidate()
    },
  })
}
